import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent, QuickAddSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { DialogCloseEvent } from '@theme/dialog';
import { openQuickAddDrawer, cancelQuickAddDrawer } from '@theme/quick-add-opener';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLButtonElement} [sizeButton] - Opens the size drawer, absent without a size option
 * @property {HTMLElement} [sizeValue] - The size button's label, swapped for the picked value
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  // Only the bar itself is required. On a sold out variant the add button is replaced by the
  // back in stock CTA, and the quantity display -- which lives inside that button -- goes
  // with it. Demanding them would throw and take the whole bar down.
  requiredRefs = ['stickyBar'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #awaitingSizeSelection = false;

  /**
   * Whether an add is on its way to the cart. Kept here and not only on the button: a variant
   * update morphs the bar back to the server's markup, which knows nothing of a request in
   * flight.
   *
   * @type {boolean}
   */
  #addToCartBusy = false;

  /**
   * Identifies the latest size drawer opening. Two taps in a row abort the first opening,
   * which then comes back as `cancelled` while the second drawer is already on screen:
   * without this token the stale call would clear the wait the new one is relying on.
   *
   * @type {number}
   */
  #sizeSelectionToken = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    target?.addEventListener(ThemeEvents.addToCartBusy, this.#handleAddToCartBusy, { signal });

    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    // The drawer is inserted into `body`, outside of the section: the event is listened
    // for on `document` rather than on the element, or it would never reach us.
    document.addEventListener(QuickAddSelectEvent.eventName, this.#handleQuickAddSelect, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    // Only its own opening: aborting unconditionally would kill a product card's drawer.
    cancelQuickAddDrawer(this);
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const footer = document.querySelector('footer');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.click();
  };

  /**
   * Mirrors the product form's waiting state on the bar's own button.
   *
   * The bar proxies its click to the page's button, so the request is not its own: the state
   * has to come back the other way for the visitor to see that the tap was taken.
   *
   * @param {CustomEvent} event - The add to cart busy event.
   */
  #handleAddToCartBusy = (event) => {
    const { busy, productId } = event.detail?.data ?? {};
    // Bundle and upsell cards live in the same section: their adds are not the page's.
    if (productId && productId !== this.dataset.productId) return;

    this.#addToCartBusy = Boolean(busy);
    this.#reflectAddToCartBusy();
  };

  /**
   * Puts the waiting state on the bar's own button.
   *
   * The DOM is read directly rather than through `refs`, which a MutationObserver refreshes
   * asynchronously: this also runs right after a morph, where `refs` can still point at the
   * nodes that were replaced.
   */
  #reflectAddToCartBusy() {
    const addToCartButton = this.querySelector('[ref="addToCartButton"]');
    if (!(addToCartButton instanceof HTMLButtonElement)) return;

    addToCartButton.toggleAttribute('data-busy', this.#addToCartBusy);
    // Closed while it waits, like the page's own button: the bar is where a rage click on a
    // slow cart lands, and each tap would add the line once more. Once the wait is over the
    // page's button decides, since it is the one that knows whether the variant is available.
    if (this.#addToCartBusy) {
      addToCartButton.disabled = true;
      addToCartButton.setAttribute('aria-busy', 'true');
    } else {
      addToCartButton.disabled = Boolean(this.#targetAddToCartButton?.disabled);
      addToCartButton.removeAttribute('aria-busy');
    }
  }

  /**
   * Opens the size drawer, the same one the product cards use.
   *
   * `select` mode: the drawer hands the picked variant back instead of adding it, so the
   * page's variant picker stays the single source of truth for what the add button sends.
   * `compact` drops what the page already shows -- the product, its price -- and the CTA with
   * it: tapping a size hands it straight back.
   */
  handleSizeClick = async () => {
    const productUrl = this.dataset.productUrl;
    if (!productUrl) return;

    const token = ++this.#sizeSelectionToken;
    this.#awaitingSizeSelection = true;

    const status = await openQuickAddDrawer({
      productUrl,
      variantId: this.dataset.currentVariantId,
      mode: 'select',
      layout: 'compact',
      owner: this,
    });

    // A newer click has taken over the wait in the meantime. It owns the drawer now, so this
    // stale call must not touch the flag: clearing it would make the visitor's pick be ignored.
    if (token !== this.#sizeSelectionToken) return;

    if (status !== 'opened') {
      // Nothing to pick, a failed fetch, or an opening that got cancelled: no answer will come
      // in any of these cases, and the wait must not stay open and catch a choice made from a
      // product card instead.
      this.#awaitingSizeSelection = false;
      return;
    }

    this.#stopWaitingOnClose(token);
  };

  /**
   * Closes the wait when the drawer is dismissed without a pick.
   *
   * Left open, the bar would keep listening on `document` and would apply to the page a size
   * picked later from another opener -- a bundle card, a recommendation -- for the same product.
   * `dialog:close` does not bubble, so it is listened for on the drawer itself; the drawer is
   * looked up in the document because `openQuickAddDrawer` guarantees a single one at a time.
   *
   * @param {number} token - The opening this wait belongs to.
   */
  #stopWaitingOnClose(token) {
    const drawer = document.querySelector('quick-add-drawer-component');
    if (!drawer) return;

    drawer.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        // A pick already closed the wait, or a newer opening reopened one: nothing to clear.
        if (token !== this.#sizeSelectionToken) return;
        this.#awaitingSizeSelection = false;
      },
      { once: true, signal: this.#abortController.signal }
    );
  }

  /**
   * Applies the size picked in the drawer to the page's variant picker.
   * @param {Event} event - The quick add select event.
   */
  #handleQuickAddSelect = (event) => {
    if (!this.#awaitingSizeSelection) return;

    const detail = /** @type {any} */ (event).detail;
    // The drawer is shared with the product cards: only the answer to our own opening
    // counts, and only for the product of the page.
    if (String(detail?.data?.productId ?? '') !== this.dataset.productId) return;

    this.#awaitingSizeSelection = false;

    const options = detail?.resource?.options;
    const position = this.dataset.sizeOptionPosition;
    this.#showPickedSize((position ? options?.[position] : null) ?? detail?.data?.label);
    this.#selectOptionValues(options);
  };

  /**
   * Records a size the visitor actually picked and shows it on the button.
   *
   * The attribute lives on the host, outside of the morphed area: the server always renders
   * the default label back on a variant change, so the value is reapplied from here.
   *
   * @param {string | null | undefined} value - The picked option value.
   */
  #showPickedSize(value) {
    if (!value) return;

    this.dataset.pickedSize = value;
    this.#updateSizeLabel();
  }

  /**
   * Puts the picked size on the button, in place of the invitation to pick one.
   *
   * The accessible name keeps the option name -- "Size XL" rather than a bare "XL", which
   * does not say what it is a value of -- while still containing the button's visible text.
   */
  #updateSizeLabel() {
    const picked = this.dataset.pickedSize;
    if (!picked) return;

    // `refs` is refreshed by a MutationObserver, hence asynchronously: right after a morph
    // it can still point at the replaced nodes. Read the DOM directly here.
    const sizeButton = this.querySelector('[ref="sizeButton"]');
    const sizeValue = this.querySelector('[ref="sizeValue"]');
    if (!sizeValue || !sizeButton) return;

    sizeValue.textContent = picked;

    const optionName = this.dataset.sizeOption;
    if (optionName) sizeButton.setAttribute('aria-label', `${optionName} ${picked}`);
  }

  /**
   * Reads the size out of an option value picked in the page's own variant picker.
   *
   * Only the size option counts: a colour change must not reveal a size the visitor never
   * picked, it stays the one the theme preselected.
   *
   * @param {string | undefined} optionValueId - The option value id carried by the event.
   */
  #showSizeFromOptionValue(optionValueId) {
    const position = this.dataset.sizeOptionPosition;
    const productId = this.dataset.productId;
    if (!optionValueId || !position || !productId) return;

    const input = document.querySelector(
      `variant-picker[data-product-id="${CSS.escape(productId)}"] input[data-option-value-id="${CSS.escape(
        optionValueId
      )}"]`
    );

    if (!(input instanceof HTMLInputElement)) return;
    if (!input.dataset.inputId?.startsWith(`${position}-`)) return;

    this.#showPickedSize(input.value);
  }

  /**
   * Checks the matching values in the page's variant picker, which then updates the whole
   * section -- price, availability and the bar included -- through its own flow.
   *
   * The drawer only renders the size option when it recognises one, and the button opening it
   * does not exist without one: in practice the loop runs over a single value.
   *
   * @param {Record<string, string> | undefined} options - Picked values, keyed by option position.
   */
  #selectOptionValues(options) {
    const productId = this.dataset.productId;
    if (!options || !productId) return;

    const pickers = document.querySelectorAll(`variant-picker[data-product-id="${CSS.escape(productId)}"]`);
    if (!pickers.length) return;

    for (const [position, value] of Object.entries(options)) {
      for (const picker of pickers) {
        const input = /** @type {HTMLInputElement | undefined} */ (
          [...picker.querySelectorAll(`input[data-input-id^="${CSS.escape(position)}-"]`)].find(
            (candidate) => /** @type {HTMLInputElement} */ (candidate).value === value
          )
        );

        if (!input || input.checked) continue;

        // A click, not a `checked = true`: the picker listens for `change`, and this is the
        // very path a visitor takes when picking a size from the page.
        input.click();
        break;
      }
    }
  }

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    // The morph puts the bar back in the state the server rendered it in, which knows nothing
    // of an add still in flight: without this the spinner would vanish mid-request and the bar
    // would take a second tap.
    if (this.#addToCartBusy) this.#reflectAddToCartBusy();

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
    // The server knows nothing of the visitor's choice: the label comes back from the response
    // as "Select your size", so the picked size has to be put back on it.
    this.#updateSizeLabel();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    // The product cards of the bundle block and of the upsell live in the same section: their
    // own variant choice bubbles up here and must not be taken for the page's own.
    const source = event.target instanceof HTMLElement ? event.target : null;
    if (!source || source.closest('product-card')) return;
    if (source.dataset.productId && source.dataset.productId !== this.dataset.productId) return;

    const { id: optionValueId, variantId } = event.detail.resource ?? {};

    // `resource.id` is the option value id, not the variant id: storing it here would send the
    // drawer a `?variant=` Shopify cannot resolve.
    if (variantId) this.dataset.currentVariantId = variantId;

    this.#showSizeFromOptionValue(optionValueId);
  };

  /**
   * Forgets the current variant when the picked combination does not resolve to one.
   *
   * The bar no longer shows the variant title: there is nothing left to rewrite here, but the
   * id has to be forgotten so the drawer does not reopen on a dead variant.
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    // Absent on a sold out variant, where the back in stock CTA has taken the button's place:
    // there is no quantity to display next to an alert sign-up.
    if (!addToCartButton || !quantityDisplay || !quantityNumber) return;

    // A button held closed by an add in flight is still a button on an available variant.
    const available = !addToCartButton.disabled || addToCartButton.hasAttribute('data-busy');

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
